/* Public, locale-aware Shopify Ajax prices for the three US soup-kit cards.
 * The same code runs inline in the local preview and as a scoped theme asset
 * when the article is integrated. No price is embedded in the article body.
 */
(function (global) {
  'use strict';

  const HANDLES = new Set([
    'confinement-soup-10-day-pack',
    'confinement-soup-20-day-pack',
    'confinement-soup-30-day-pack',
  ]);
  const LOCALES = new Set(['en-us', 'zh-us']);

  function validCents(value) {
    return Number.isSafeInteger(value) && value >= 0;
  }

  function money(cents, locale, alwaysTwoDecimals) {
    return new Intl.NumberFormat(locale === 'zh-us' ? 'zh-CN' : 'en-US', {
      style: 'currency',
      currency: 'USD',
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: alwaysTwoDecimals || cents % 100 ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }

  function pricePresentation(product, handle, days, locale, currency) {
    if (!LOCALES.has(locale) || !HANDLES.has(handle) || currency !== 'USD') {
      throw new Error('Unsupported market');
    }
    if (!Number.isSafeInteger(days) || days <= 0 || product?.handle !== handle || !Array.isArray(product.variants)) {
      throw new Error('Invalid product');
    }
    const available = product.variants.filter((variant) => variant.available === true);
    const selected = available[0];
    if (!selected || !validCents(selected.price) || !Number.isSafeInteger(selected.id)) {
      throw new Error('No priced available variant');
    }
    const compare = validCents(selected.compare_at_price) && selected.compare_at_price > selected.price
      ? selected.compare_at_price : null;
    const sameOffer = available.every((variant) =>
      variant.price === selected.price && (variant.compare_at_price ?? null) === (selected.compare_at_price ?? null)
    );
    const hasDifferentOffer = !sameOffer;
    const isChinese = locale === 'zh-us';
    const perDayCents = Math.round(selected.price / days);
    const variantName = hasDifferentOffer
      ? (selected.title && selected.title !== 'Default Title' ? selected.title : (isChinese ? '所选规格' : 'Selected option'))
      : null;
    return {
      current: money(selected.price, locale, false),
      compare: compare === null ? null : money(compare, locale, false),
      saving: compare === null ? null : (isChinese ? `省 ${money(compare - selected.price, locale, false)}` : `Save ${money(compare - selected.price, locale, false)}`),
      daily: isChinese ? `每日 ${money(perDayCents, locale, true)}（仅汤包）` : `${money(perDayCents, locale, true)}/day, kit only`,
      variantName,
      variantId: hasDifferentOffer ? selected.id : null,
    };
  }

  async function fetchJson(path) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(path, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Price unavailable');
      return await response.json();
    } finally {
      clearTimeout(timer);
    }
  }

  function renderFailure(card, locale) {
    const status = card.querySelector('[data-gm-status]');
    const prices = card.querySelector('[data-gm-prices]');
    const link = card.querySelector('[data-gm-product-link]');
    if (status) {
      status.hidden = false;
      status.textContent = locale === 'zh-us' ? '请前往商品页查看现价' : 'View current price on product page';
    }
    if (prices) prices.hidden = true;
    if (link && link.dataset.gmBaseHref) link.href = link.dataset.gmBaseHref;
  }

  function renderPrice(card, presentation) {
    const link = card.querySelector('[data-gm-product-link]');
    const status = card.querySelector('[data-gm-status]');
    const prices = card.querySelector('[data-gm-prices]');
    const current = card.querySelector('[data-gm-current]');
    const compare = card.querySelector('[data-gm-compare]');
    const saving = card.querySelector('[data-gm-saving]');
    const daily = card.querySelector('[data-gm-daily]');
    const variant = card.querySelector('[data-gm-variant]');
    if (!link || !status || !prices || !current || !compare || !saving || !daily || !variant) return;
    current.textContent = presentation.current;
    compare.hidden = presentation.compare === null;
    compare.textContent = presentation.compare ?? '';
    saving.hidden = presentation.saving === null;
    saving.textContent = presentation.saving ?? '';
    daily.textContent = presentation.daily;
    variant.hidden = presentation.variantName === null;
    variant.textContent = presentation.variantName ?? '';
    const destination = new URL(link.dataset.gmBaseHref || link.href);
    if (presentation.variantId) destination.searchParams.set('variant', String(presentation.variantId));
    else destination.searchParams.delete('variant');
    link.href = destination.href;
    status.hidden = true;
    prices.hidden = false;
  }

  async function hydrate(root) {
    const locale = root?.dataset.gmLocale;
    if (!LOCALES.has(locale)) return;
    const cards = [...root.querySelectorAll('[data-gm-handle][data-gm-days]')];
    for (const card of cards) {
      const link = card.querySelector('[data-gm-product-link]');
      if (link) link.dataset.gmBaseHref = link.href;
      const status = card.querySelector('[data-gm-status]');
      if (status) status.textContent = locale === 'zh-us' ? '正在获取现价…' : 'Loading current price…';
    }
    let currency;
    try {
      const cart = await fetchJson(`/${locale}/cart.js`);
      currency = cart.currency;
      if (currency !== 'USD') throw new Error('Unexpected currency');
    } catch (_error) {
      cards.forEach((card) => renderFailure(card, locale));
      return;
    }
    await Promise.all(cards.map(async (card) => {
      try {
        const handle = card.dataset.gmHandle;
        if (!HANDLES.has(handle)) throw new Error('Unexpected product');
        const product = await fetchJson(`/${locale}/products/${handle}.js`);
        const presentation = pricePresentation(product, handle, Number(card.dataset.gmDays), locale, currency);
        renderPrice(card, presentation);
      } catch (_error) {
        renderFailure(card, locale);
      }
    }));
  }

  const api = { money, pricePresentation, hydrate };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof document !== 'undefined') {
    const start = () => document.querySelectorAll('[data-gm-soup-cards]').forEach(hydrate);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }
})(typeof window !== 'undefined' ? window : globalThis);
